import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/context/auth";

interface Project {
  id: string;
  name: string;
  description?: string;
}

interface Folder {
  id: string;
  name: string;
  description: string;
  project_id: string;
  parent_folder_id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  revised_by: string;
}

interface Assessment {
  id: string;
  name: string;
  description: string | null;
  folder_id: string;
  assessment_id: string;
  created_at: string;
  updated_at?: string;
  data?: any;
  status?: string;
  designer_completed_at?: string;
  designer_completed_by?: string;
  production_completed_at?: string;
  production_completed_by?: string;
  basu_ready_at?: string;
  type: 'risk_assessment' | 'skr_assessment' | 'work_preparation';
  project_id?: string;
  source_risk_assessment_id?: string;
}

export interface UseProjectRootContentProps {
  projectId: string;
}

export function useProjectRootContent({ projectId }: UseProjectRootContentProps) {
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjectRootContent = useCallback(async () => {
    if (!user || !projectId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch project info
      const { data: projectData, error: projectError } = await supabase
        .from("projects")
        .select("id, name, description")
        .eq("id", projectId)
        .maybeSingle();

      if (projectError) {
        throw new Error(`Could not fetch project: ${projectError.message}`);
      }

      setProject(projectData);

      // If no project is visible (no access via RLS), stop here
      if (!projectData) {
        setFolders([]);
        setAssessments([]);
        return;
      }

      // Fetch root-level folders (folders with no parent_folder_id)
      const { data: foldersData, error: foldersError } = await supabase
        .from("folders")
        .select("*")
        .eq("project_id", projectId)
        .is("parent_folder_id", null)
        .order("name");

      if (foldersError) {
        throw new Error(`Could not fetch folders: ${foldersError.message}`);
      }

      setFolders(foldersData || []);

      // Don't fetch assessments on project root level - they should only be visible within folders
      setAssessments([]);

    } catch (err: any) {
      console.error("Error fetching project root content:", err);
      setError(err.message);
      toast.error("Kunde inte hämta projektinnehåll", {
        description: err.message
      });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, user]);

  useEffect(() => {
    fetchProjectRootContent();
  }, [fetchProjectRootContent]);

  return {
    project,
    folders,
    assessments,
    isLoading,
    error,
    refetch: fetchProjectRootContent,
  };
}